import Vue from 'vue';
import moment from 'moment';
import { formatFieldsForSubmit, validateFields, checkEmptyRequiredCustomFields } from '@/utils/helpers/customFields';

const initialState = () => ({
  step: {},
  selectedProjectOption: 0,
  id: 0,
  name: '',
  isTemplate: false,
  company: null,
  retainerType: '',
  retainerTaskListName: moment().format('YYYY-MM'),
  retainerTasks: [{ id: 0, name: 'SLA' }],
  source: '',
  isDynamicProjectName: true,
  categoryId: 0,
  tags: [],
  description: '',
  projectChatChannelEnabled: false,
  people: [],
  projectOwnerId: 0,
  privacyIds: [],
  forceValidation: false,
  items: [],
  selectedItems: [],
  makeMeCreator: true,
  uncompleteItems: false,
  saveAsTemplate: true,
  shouldRenderPeoplePicker: false,
  customFields: [],
  hasInvalidFields: false,
  requiredCustomFields: [],
  isTemplateApplied: false,
  appliedTemplateId: null,
  startDate: null,
  endDate: null,
  enableProjectDates: false,
  templateDateTarget: 'start', // This is whether dates for a project created by a template are targeting the start or end date of the project
  keepOffWeekends: 0,
  templateTargetDate: moment(),
  activePages: {
    links: false,
    tasks: false,
    time: false,
    finance: false,
    notebooks: false,
    files: false,
    forms: false,
    comments: false,
    messages: false,
    milestones: false,
    allocations: false,
    riskRegister: false,
    list: false,
    board: false,
    table: false,
    gantt: false,
  },
  isBillable: true,
  isTeamworkTemplate: false,
  portfolioColumn: null,
  selectedWorkflows: [],
  projectType: window.app?.consts?.PROJECT_NORMAL ?? 'normal', // normal | tentative | projects-template
  status: '', // active | archived | deleted
});

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    activePages(state, activePages) {
      state.activePages = activePages;
    },
    step(state, step) {
      state.step = step;
    },
    id(state, id) {
      state.id = id;
    },
    name(state, name) {
      state.name = name;
    },
    isTemplate(state, isTemplate) {
      state.isTemplate = isTemplate;
    },
    categoryId(state, categoryId) {
      state.categoryId = categoryId;
    },
    tags(state, tags) {
      state.tags = tags;
    },
    company(state, company) {
      state.company = company;
    },
    projectType(state, projectType) {
      state.projectType = projectType;
    },
    retainerType(state, retainerType) {
      state.retainerType = retainerType;
    },
    retainerTaskListName(state, retainerTaskListName) {
      state.retainerTaskListName = retainerTaskListName;
    },
    retainerTasks(state, retainerTasks) {
      state.retainerTasks = retainerTasks;
    },
    source(state, source) {
      state.source = source;
    },
    isDynamicProjectName(state, isDynamicProjectName) {
      state.isDynamicProjectName = isDynamicProjectName;
    },
    description(state, description) {
      state.description = description;
    },
    makeMeCreator(state, makeMeCreator) {
      state.makeMeCreator = makeMeCreator;
    },
    uncompleteItems(state, uncompleteItems) {
      state.uncompleteItems = uncompleteItems;
    },
    items(state, items) {
      state.items = items;
    },
    selectedItems(state, selectedItems) {
      state.selectedItems = selectedItems;
    },
    saveAsTemplate(state, saveAsTemplate) {
      state.saveAsTemplate = saveAsTemplate;
    },
    projectChatChannelEnabled(state, projectChatChannelEnabled) {
      state.projectChatChannelEnabled = projectChatChannelEnabled;
    },
    people(state, people) {
      state.people = people;
    },
    projectOwnerId(state, projectOwnerId) {
      state.projectOwnerId = projectOwnerId;
    },
    privacyIds(state, privacyIds) {
      state.privacyIds = privacyIds;
    },
    forceValidation(state, forceValidation) {
      state.forceValidation = forceValidation;
    },
    shouldRenderPeoplePicker(state, shouldRenderPeoplePicker) {
      state.shouldRenderPeoplePicker = shouldRenderPeoplePicker;
    },
    customFields(state, fields) {
      state.customFields = fields;
    },
    isTemplateApplied(state, fields) {
      state.isTemplateApplied = fields;
    },
    appliedTemplateId(state, fields) {
      state.appliedTemplateId = fields;
    },
    templateDateTarget(state, templateDateTarget) {
      state.templateDateTarget = templateDateTarget;
    },
    keepOffWeekends(state, keepOffWeekends) {
      state.keepOffWeekends = keepOffWeekends;
    },
    templateTargetDate(state, templateTargetDate) {
      state.templateTargetDate = templateTargetDate;
    },
    startDate(state, startDate) {
      state.startDate = startDate;
    },
    endDate(state, endDate) {
      state.endDate = endDate;
    },
    enableProjectDates(state, enableProjectDates) {
      state.enableProjectDates = enableProjectDates;
    },
    updateField(state, field) {
      const index = state.customFields.findIndex((f) => f.customfieldId === field.id);
      if (index === -1) {
        return;
      }
      const newField = { ...state.customFields[index], ...field.data };
      Vue.set(state.customFields, index, newField);
    },
    updateValidCustomFields(state, hasInvalidFields) {
      state.hasInvalidFields = hasInvalidFields;
    },
    updateRequiredCustomField(state, { fieldId, isRequired }) {
      if (isRequired) {
        state.requiredCustomFields.push(fieldId);
      } else {
        const index = state.requiredCustomFields.findIndex((id) => id === fieldId);
        if (index !== -1) {
          state.requiredCustomFields.splice(index, 1);
        }
      }
    },
    setRequiredCustomFields(state, requiredCustomFields) {
      state.requiredCustomFields = requiredCustomFields;
    },
    selectedProjectOption(state, option) {
      state.selectedProjectOption = option;
    },
    init(state) {
      const s = initialState();
      Object.assign(state, s);
    },
    isBillable(state, isBillable) {
      state.isBillable = isBillable;
    },
    isTeamworkTemplate(state, isTeamworkTemplate) {
      state.isTeamworkTemplate = isTeamworkTemplate;
    },
    portfolioColumn(state, portfolioColumn) {
      state.portfolioColumn = portfolioColumn;
    },
    selectedWorkflows(state, selectedWorkflows) {
      state.selectedWorkflows = selectedWorkflows;
    },
    status(state, status) {
      state.status = status;
    },
  },
  actions: {
    setCustomFields(context, fields) {
      context.commit('customFields', fields);
    },
    updateCustomField(context, data) {
      context.commit('updateField', data);
    },
    updateValidCustomFields(context, data) {
      context.commit('updateValidCustomFields', data);
    },
    updateRequiredCustomField(context, data) {
      context.commit('updateRequiredCustomField', data);
    },
    setRequiredCustomFields(context, data) {
      context.commit('setRequiredCustomFields', data);
    },
    init({ commit }) {
      commit('init');
    },
    id({ commit }, id) {
      commit('id', id);
    },
    description({ commit }, description) {
      commit('description', description);
    },
    customFields({ commit }, customFields) {
      commit('customFields', customFields);
    },
    isTemplate({ commit }, isTemplate) {
      commit('isTemplate', isTemplate);
    },
    company({ commit }, company) {
      commit('company', company);
    },
    projectType({ commit }, projectType) {
      commit('projectType', projectType);
    },
    retainerType({ commit }, retainerType) {
      commit('retainerType', retainerType);
    },
    retainerTaskListName({ commit }, retainerTaskListName) {
      commit('retainerTaskListName', retainerTaskListName);
    },
    retainerTasks({ commit }, retainerTasks) {
      commit('retainerTasks', retainerTasks);
    },
    source({ commit }, source) {
      commit('source', source);
    },
    isDynamicProjectName({ commit }, isDynamicProjectName) {
      commit('isDynamicProjectName', isDynamicProjectName);
    },
    categoryId({ commit }, categoryId) {
      commit('categoryId', categoryId);
    },
    shouldRenderPeoplePicker({ commit }, shouldRenderPeoplePicker) {
      commit('shouldRenderPeoplePicker', shouldRenderPeoplePicker);
    },
    tags({ commit }, tags) {
      commit('tags', tags);
    },
    projectOwnerId({ commit }, projectOwnerId) {
      commit('projectOwnerId', projectOwnerId);
    },
    step({ commit }, step) {
      commit('step', step);
    },
    people({ commit }, people) {
      commit('people', people);
    },
    isTemplateApplied({ commit }, isTemplateApplied) {
      commit('isTemplateApplied', isTemplateApplied);
    },
    appliedTemplateId({ commit }, appliedTemplateId) {
      commit('appliedTemplateId', appliedTemplateId);
    },
    templateDateTarget({ commit }, templateDateTarget) {
      commit('templateDateTarget', templateDateTarget);
    },
    forceValidation({ commit }, forceValidation) {
      commit('forceValidation', forceValidation);
    },
    privacyIds({ commit }, privacyIds) {
      commit('privacyIds', privacyIds);
    },
    name({ commit }, name) {
      commit('name', name);
    },
    startDate({ commit }, startDate) {
      commit('startDate', startDate);
    },
    endDate({ commit }, endDate) {
      commit('endDate', endDate);
    },
    enableProjectDates({ commit }, enableProjectDates) {
      commit('enableProjectDates', enableProjectDates);
    },
    templateTargetDate({ commit }, templateTargetDate) {
      commit('templateTargetDate', templateTargetDate);
    },
    keepOffWeekends({ commit }, keepOffWeekends) {
      commit('keepOffWeekends', keepOffWeekends);
    },
    selectedItems({ commit }, selectedItems) {
      commit('selectedItems', selectedItems);
    },
    makeMeCreator({ commit }, makeMeCreator) {
      commit('makeMeCreator', makeMeCreator);
    },
    activePages({ commit }, activePages) {
      commit('activePages', activePages);
    },
    selectedProjectOption({ commit }, option) {
      commit('selectedProjectOption', option);
    },
    setIsBillable({ commit }, value) {
      commit('isBillable', value);
    },
    setIsTeamworkTemplate({ commit }, value) {
      commit('isTeamworkTemplate', value);
    },
    setPortfolioColumn({ commit }, value) {
      commit('portfolioColumn', value);
    },
    selectedWorkflows({ commit }, value) {
      commit('selectedWorkflows', value);
    },
    status({ commit }, value) {
      commit('status', value);
    },
  },
  getters: {
    selectedPeopleIds(state) {
      return (state.people || []).map((x) => x.id);
    },
    isArchived(state) {
      return state.status === 'archived';
    },
    projectTypeForPayload(state) {
      if (state.isTemplate) {
        return window.app.consts.PROJECT_TEMPLATE;
      }
      if (state.projectType === window.app.consts.PROJECT_TENTATIVE) {
        return window.app.consts.PROJECT_TENTATIVE;
      }
      return window.app.consts.PROJECT_NORMAL;
    },
    payload: (state, getters, _rootState, rootGetters) => {
      const features = rootGetters['account/features'];

      const isTemplate = state.isTemplate;

      let grantAccessTo = state.privacyIds;
      if (grantAccessTo.length === 0) {
        grantAccessTo = [0];
      } else if (!features.includes('templateprivacy') && isTemplate && grantAccessTo[0] !== 0) {
        grantAccessTo = [-1];
      }

      let company = state.company;
      if (Array.isArray(company)) {
        company = state.company[0];
      }

      const data = {
        description: state.description,
        companyId: company ? company.id : 0,
        name: state.name,
        // chatChannelEnabled: state.projectChatChannelEnabled ? 1 : 0,
        tagIds: (state.tags || []).length > 0 ? state.tags.map((x) => x.id).join(',') : '',
        'grant-access-to': grantAccessTo.join(','),
        private: false,
        'category-id': state.categoryId,
        people: getters.selectedPeopleIds.join(','),
        projectOwnerId: state.projectOwnerId,
        customFields: formatFieldsForSubmit(state.customFields),
        isBillable: state.isBillable,
        projectType: getters.projectTypeForPayload,
        // portfolioColumnId: state.portfolioColumn ? state.portfolioColumn.id : null,
      };
      if (state.enableProjectDates && state.startDate) {
        data['start-date'] = moment(state.startDate).format('YYYYMMDD');
      }
      if (state.enableProjectDates && state.endDate) {
        data['end-date'] = moment(state.endDate).format('YYYYMMDD');
      }
      if (grantAccessTo[0] > 0) {
        data.private = true;
      }
      if (state.company && state.company.id === 0 && state.company.name.length > 0) {
        data.newCompany = state.company.name;
      }
      // Only pass default workflow when creating new project
      if (state.selectedWorkflows.length > 0 && state.id === 0) {
        // @TODO: This param needs to be converted to an array for multiple workflows
        data.workflowId = state.selectedWorkflows[0].id;
      }
      return data;
    },
    templatePayload: (state, getters, { account }) => {
      const select = state.selectedItems;
      let company = state.company;
      if (Array.isArray(company)) {
        company = state.company[0];
      }

      const data = {
        newFromTemplate: !state.isTemplate,
        targetDate: state.templateTargetDate.format('YYYYMMDD'),
        templateDateTarget: state.templateDateTarget,
        id: state.id,
        installationId: account.id,
        sourceInstallationId: account.id,
        'cloneproject-action': 'copy',
        cloneProjectName: state.name,
        description: state.description,
        copyTasks: select.includes('tasks') ? 'YES' : 'NO',
        copyTasklists: select.includes('tasks') ? 'YES' : 'NO',
        copyMilestones: select.includes('milestones') ? 'YES' : 'NO',
        copyAllocations: select.includes('allocations') ? 'YES' : 'NO',
        copyMessages: select.includes('messages') ? 'YES' : 'NO',
        copyFiles: select.includes('files') ? 'YES' : 'NO',
        copyLinks: select.includes('links') ? 'YES' : 'NO',
        copyNotebooks: select.includes('notebooks') ? 'YES' : 'NO',
        copyTimelogs: select.includes('timelogs') ? 'YES' : 'NO',
        copyInvoices: select.includes('invoices') ? 'YES' : 'NO',
        copyExpenses: select.includes('expenses') ? 'YES' : 'NO',
        copyRisks: select.includes('risks') ? 'YES' : 'NO',
        copyPeople: select.includes('people') ? 'YES' : 'NO',
        copyForms: select.includes('forms') ? 'YES' : 'NO',
        copyAutomations: select.includes('automations') ? 'YES' : 'NO',
        copyComments: select.includes('comments') ? 'YES' : 'NO',
        copyFollowers: select.includes('followers') ? 'YES' : 'NO',
        copyCustomItems: select.includes('customItems') ? 'YES' : 'NO',
        copyProjectRoles: 'YES',
        copyProjectUpdates: 'NO',
        copyProjectPrivacy: 'YES',
        copyLogo: 'YES',
        copyBudgets: 'NO',
        daysOffset: 0,
        keepOffWeekends: state.keepOffWeekends,
        createActivityLog: 'YES',
        createItemsUsingCurrentUser: state.makeMeCreator ? 'YES' : 'NO',
        uncomplete: 'YES',
        companyId: company ? company.id : 0,
        newCompanyName: company ? company.name : '',
        tagIds: (state.tags || []).length > 0 ? state.tags.map((x) => x.id).join(',') : '',
        customFields: JSON.stringify(formatFieldsForSubmit(state.customFields)),
        'use-tasks': state.activePages.tasks,
        'use-milestones': state.activePages.milestones,
        'use-messages': state.activePages.messages,
        'use-files': state.activePages.files,
        'use-time': state.activePages.time,
        'use-notebooks': state.activePages.notebooks,
        'use-riskregister': state.activePages.riskRegister,
        'use-links': state.activePages.links,
        'use-finance': state.activePages.finance,
        'use-comments': state.activePages.comments,
        'use-forms': state.activePages.forms,
        'category-id': state.categoryId,
        peopleIds: getters.selectedPeopleIds.join(','),
        projectOwnerId: state.projectOwnerId,
        toTemplate: !!(state.isTemplate && state.isTemplateApplied),
        isBillable: state.isBillable,
        projectType: getters.projectTypeForPayload,
        // portfolioColumnId: state.portfolioColumn ? state.portfolioColumn.id : null,
      };

      // Only pass default workflow when creating new project from template
      if (state.selectedWorkflows.length > 0 && state.id === 0) {
        // @TODO: This param needs to be converted to an array for multiple workflows
        data.workflowId = state.selectedWorkflows[0].id;
      }

      return data;
    },
    teamworkTemplatePayload: (state, getters, { account }) => {
      let company = state.company;
      if (Array.isArray(company)) {
        company = state.company[0];
      }

      return {
        newFromTemplate: !state.isTemplate,
        targetDate: state.templateTargetDate.format('YYYYMMDD'),
        templateDateTarget: state.templateDateTarget,
        templateId: state.appliedTemplateId,
        installationId: account.id,
        sourceInstallationId: account.id,
        'cloneproject-action': 'copy',
        cloneProjectName: state.name,
        description: state.description,
        daysOffset: 0,
        keepOffWeekends: state.keepOffWeekends,
        createActivityLog: 'YES',
        createItemsUsingCurrentUser: state.makeMeCreator ? 'YES' : 'NO',
        uncomplete: 'NO',
        companyId: company ? company.id : 0,
        newCompanyName: company ? company.name : '',
        tagIds: (state.tags || []).length > 0 ? state.tags.map((x) => x.id).join(',') : '',
        customFields: formatFieldsForSubmit(state.customFields),
        peopleIds: getters.selectedPeopleIds.join(','),
        projectOwnerId: state.projectOwnerId,
        toTemplate: !!(state.isTemplate && state.isTemplateApplied),
        isBillable: state.isBillable,
        projectType: getters.projectTypeForPayload,
        // portfolioColumnId: state.portfolioColumn ? state.portfolioColumn.id : null,
      };
    },
    hasInvalidCustomFields: (state) => state.customFields.some((field) => !field.isValid),
    customFieldsValidationErrors: (state) => validateFields(state.customFields),
    hasEmptyRequiredCustomFields: (state) =>
      checkEmptyRequiredCustomFields(state.customFields, state.requiredCustomFields),
  },
};
